import React, { useEffect, useState, useContext } from "react";
import { auth, functions } from "../../../config/firebaseConfig";
import { httpsCallable } from "firebase/functions";
import UserManagementPopUp from "./UserManagementPopUp";
import DeletionRequestsModal from "./DeletionRequestsModal";
import Pagination from "../Pagination";
import Table from "./UserManagementTable";
import "../../../styles/CreateButton.css";
import { UserContext } from "../../../App";
import "../../../styles/common.css";
import MoreOptionsPane from "../../MoreOptionsPane";
import "../../../styles/MoreOptionsButton.css";
import { getPerformance, trace } from "firebase/performance";

const formatDate = (dateField) => {
  if (!dateField) return "N/A";
  if (dateField && dateField._seconds) {
    return new Date(dateField._seconds * 1000).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
  if (dateField instanceof Date) {
    return dateField.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
  if (typeof dateField === "string") {
    return new Date(dateField).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
  return "Invalid Date";
};

const columns = [
  { key: "firstName", label: "First Name", sortable: true },
  { key: "lastName", label: "Last Name", sortable: true },
  { key: "role", label: "Role", sortable: true },
  { key: "username", label: "Username", sortable: true },
  { key: "email", label: "Email", sortable: true },
  { key: "registered", label: "Created", sortable: false },
  { key: "lastLogin", label: "Last Login", sortable: false },
];

const UserManagementPage = () => {
  const {} = useContext(UserContext); // Remove 'role' from here
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const usersPerPage = 15; // Changed from 20 to 15
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [originalUserData, setOriginalUserData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [showMoreOptionsPane, setShowMoreOptionsPane] = useState(false);
  const [showDeletionRequestsModal, setShowDeletionRequestsModal] =
    useState(false);
  const [isDeletingUsers, setIsDeletingUsers] = useState(false);

  const requestSort = (field) => {
    setSortField(field);
    setSortDirection((prevDirection) =>
      field === sortField && prevDirection === "asc" ? "desc" : "asc"
    );
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchFilteredUsers();
  }, [currentPage, usersPerPage, sortField, sortDirection]);

  const fetchFilteredUsers = async () => {
    const fetchFilteredUsersTrace = trace(
      getPerformance(),
      "fetchFilteredUsers"
    );
    fetchFilteredUsersTrace.start();
    setIsLoading(true);
    console.log(
      `Fetching users with filter: "${filterText}", sort field: ${sortField}, direction: ${sortDirection}`
    );
    try {
      const filterUsersFunction = httpsCallable(functions, "filterUsers");
      const result = await filterUsersFunction({
        filterText,
        page: currentPage,
        usersPerPage,
        sortField,
        sortDirection,
        caseInsensitive: true,
      });
      console.log("Fetched users:", result.data.users);
      if (Array.isArray(result.data.users)) {
        setUsers(result.data.users);
        setTotalPages(result.data.totalPages);

        // Adjust current page if it's greater than the total pages
        if (currentPage > result.data.totalPages) {
          setCurrentPage(Math.max(1, result.data.totalPages));
        }
      } else {
        console.error("Unexpected response format:", result.data);
        setUsers([]);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching filtered users:", error);
      setUsers([]);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
      fetchFilteredUsersTrace.stop();
    }
  };

  const handleSelectItem = (user) => {
    setSelectedUsers((prevSelected) => {
      const isSelected = prevSelected.some((u) => u.uid === user.uid);
      if (isSelected) {
        return prevSelected.filter((u) => u.uid !== user.uid);
      } else {
        return [...prevSelected, user];
      }
    });
  };

  const handleBulkSelect = (event) => {
    if (event.target.checked) {
      setSelectedUsers(users);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleCreateUserClick = () => {
    setSelectedUser({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      username: "",
      role: "student",
    });
    setOriginalUserData(null);
    setIsEditMode(false);
    setFormErrors({}); // Reset form errors
    setIsModalOpen(true);
  };

  const handleSelectUser = (user) => {
    console.log("Selected user:", user);
    setSelectedUser({
      uid: user.uid || user.uid,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      role: user.role,
      password: "",
    });
    setOriginalUserData(user);
    setIsEditMode(true);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedUser((prevUser) => ({
      ...prevUser,
      [name]: value,
    }));
  };

  const handleCreateUser = async (formData) => {
    try {
      const createUserFunction = httpsCallable(functions, "createUser");
      const result = await createUserFunction(formData);
      const newUser = result.data;

      const createdUser = {
        uid: newUser.uid,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        role: formData.role,
        registered: new Date(),
        updatedAt: new Date(),
        emailLower: formData.email.toLowerCase(),
        firstNameLower: formData.firstName.toLowerCase(),
        lastNameLower: formData.lastName.toLowerCase(),
        usernameLower: formData.username.toLowerCase(),
      };

      setUsers((prevUsers) => [...prevUsers, createdUser]);
      alert("User created successfully");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error creating user:", error);
      if (error.message.includes("username is already in use")) {
        throw new Error("The username is already in use.");
      } else if (error.message.includes("email address is already in use")) {
        throw new Error(
          "The email address is already in use by another account."
        );
      } else {
        throw new Error(`Failed to create user: ${error.message}`);
      }
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    try {
      console.log("Updating user:", updatedUser);
      if (!updatedUser.uid) {
        throw new Error("Invalid user ID");
      }

      const updateUserFunction = httpsCallable(functions, "updateUser");
      const result = await updateUserFunction({
        uid: updatedUser.uid,
        email: updatedUser.email,
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        username: updatedUser.username,
        role: updatedUser.role,
        password: updatedUser.password,
      });

      console.log("Update result:", result);

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.uid === updatedUser.uid
            ? {
                ...user,
                ...updatedUser,
                emailLower: updatedUser.email.toLowerCase(),
                firstNameLower: updatedUser.firstName.toLowerCase(),
                lastNameLower: updatedUser.lastName.toLowerCase(),
                usernameLower: updatedUser.username.toLowerCase(),
              }
            : user
        )
      );

      return result;
    } catch (error) {
      console.error("Error updating user:", error);
      throw new Error(error.message);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const formData = {
      uid: selectedUser?.uid,
      email: event.target.email?.value,
      password: event.target.password?.value,
      firstName: event.target.firstName?.value,
      lastName: event.target.lastName?.value,
      username: event.target.username?.value || "",
      role: selectedUser?.role,
    };

    console.log("Form data:", formData);

    if (isEditMode && originalUserData) {
      const isChanged = Object.keys(formData).some(
        (key) => formData[key] !== originalUserData[key]
      );
      if (!isChanged) {
        console.log("No changes detected, form submission aborted.");
        return;
      }
    }

    try {
      if (isEditMode) {
        await handleUpdateUser(formData);
      } else {
        await handleCreateUser(formData);
      }
      setSelectedUser(null);
      setIsModalOpen(false);
      setFormErrors({});
    } catch (error) {
      console.error("Error submitting the form:", error);
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        general: error.message,
      }));
    }
  };

  const handleDeleteConfirm = () => {
    handleDeleteUser();
    setShowMoreOptionsPane(false);
  };

  const handleDeleteUser = async () => {
    if (selectedUsers.length > 0) {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete ${selectedUsers.length} users?`
      );
      if (confirmDelete) {
        try {
          const deleteMultipleUsersFunction = httpsCallable(
            functions,
            "deleteMultipleUsers"
          );
          await deleteMultipleUsersFunction({
            uids: selectedUsers.map((user) => user.uid),
            currentUserUid: auth.currentUser.uid,
          });
          setUsers((prevUsers) =>
            prevUsers.filter(
              (user) =>
                !selectedUsers.some((selected) => selected.uid === user.uid)
            )
          );
          setSelectedUsers([]);
        } catch (error) {
          alert(`Failed to delete selected users: ${error.message}`);
        }
      }
    } else if (selectedUser) {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete user: ${selectedUser.email}?`
      );
      if (confirmDelete) {
        try {
          const deleteUserFunction = httpsCallable(functions, "deleteUser");
          await deleteUserFunction({
            uid: selectedUser.uid,
            currentUserUid: auth.currentUser.uid,
          });
          setUsers((prevUsers) =>
            prevUsers.filter((user) => user.uid !== selectedUser.uid)
          );
          setSelectedUser(null);
        } catch (error) {
          alert(`Failed to delete user: ${error.message}`);
        }
      }
    } else {
      alert("No user selected");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleSearch = () => {
    console.log(`Searching with filter: "${filterText}"`);
    setCurrentPage(1); // Reset to first page when searching
    fetchFilteredUsers();
  };

  const handleDeleteRequests = () => {
    setShowDeletionRequestsModal(true);
    setShowMoreOptionsPane(false);
  };

  const handleBulkDeleteRequests = async (usersToDelete) => {
    setIsDeletingUsers(true);
    try {
      const deleteMultipleUsersFunction = httpsCallable(
        functions,
        "deleteMultipleUsers"
      );
      await deleteMultipleUsersFunction({
        uids: usersToDelete.map((user) => user.uid),
        currentUserUid: auth.currentUser.uid,
      });

      // Update local state
      setUsers((prevUsers) =>
        prevUsers.filter(
          (user) => !usersToDelete.some((toDelete) => toDelete.uid === user.uid)
        )
      );
      setShowDeletionRequestsModal(false);
    } catch (error) {
      alert(`Failed to delete users: ${error.message}`);
    } finally {
      setIsDeletingUsers(false);
    }
  };

  return (
    <div className="user-management-page">
      <div className="main-content">
        <div className="title-banner"></div>
        <div className="top-controls">
          <button
            className="create-user-button"
            onClick={handleCreateUserClick}
          >
            <span className="button-text-desktop">Create a New User</span>
            <span className="button-text-mobile">Create</span>
          </button>
          <div className="filter-container">
            <input
              type="text"
              placeholder="Filter users..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
            <button onClick={handleSearch} className="search-button">
              🔍
            </button>
          </div>
        </div>
        <Table
          data={users}
          columns={columns}
          selectedItems={selectedUsers}
          handleSelectItem={handleSelectItem}
          handleBulkSelect={handleBulkSelect}
          requestSort={(field) => {
            setSortField(field);
            setSortDirection((prevDirection) =>
              field === sortField && prevDirection === "asc" ? "desc" : "asc"
            );
            setCurrentPage(1);
          }}
          sortField={sortField}
          sortDirection={sortDirection}
          onRowClick={handleSelectUser}
          isLoading={isLoading}
          formatters={{
            registered: (value) => formatDate(value),
            lastLogin: (value) => formatDate(value),
          }}
        />
        <div className="button-container">
          <button
            className="more-options-button"
            onClick={() => setShowMoreOptionsPane(true)}
          >
            More Options...
          </button>
        </div>
        {showMoreOptionsPane && (
          <MoreOptionsPane
            options={[
              { label: "Delete", action: handleDeleteConfirm },
              { label: "Delete Requests", action: handleDeleteRequests },
            ]}
            onClose={() => setShowMoreOptionsPane(false)}
          />
        )}
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={setCurrentPage}
        />
        <UserManagementPopUp
          show={isModalOpen}
          handleClose={handleCloseModal}
          formData={selectedUser}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          formErrors={formErrors}
          isEditMode={isEditMode}
          roleOptions={[
            "student",
            "studentplus",
            "teacher",
            "teacherplus",
            "admin",
            "technician",
            "technicianplus",
            "designer",
          ]}
        />
        <DeletionRequestsModal
          show={showDeletionRequestsModal}
          handleClose={() => setShowDeletionRequestsModal(false)}
          users={users}
          onBulkDelete={handleBulkDeleteRequests}
          isLoading={isDeletingUsers}
        />
      </div>
    </div>
  );
};

export default UserManagementPage;
